import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";
import { AppLayout } from "./components/AppLayout";
import { LaunchIntentBoundary } from "./components/LaunchIntentBoundary";
import { OnboardingGuard } from "./components/OnboardingGuard";
import { Dashboard } from "./pages/Dashboard";
import { useTranslation } from "react-i18next";

const Decks = lazy(() => import("./pages/Decks").then((m) => ({ default: m.Decks })));
const DeckView = lazy(() => import("./pages/DeckView").then((m) => ({ default: m.DeckView })));
const Drill = lazy(() => import("./pages/Drill").then((m) => ({ default: m.Drill })));
const Settings = lazy(() => import("./pages/Settings").then((m) => ({ default: m.Settings })));
const Tutor = lazy(() => import("./pages/Tutor").then((m) => ({ default: m.Tutor })));
const Onboarding = lazy(() => import("./pages/Onboarding").then((m) => ({ default: m.Onboarding })));
const Profile = lazy(() => import("./pages/Profile").then((m) => ({ default: m.Profile })));
const References = lazy(() => import("./pages/References").then((m) => ({ default: m.References })));
const Library = lazy(() => import("./pages/Library").then((m) => ({ default: m.Library })));
const AiTutor = lazy(() => import("./pages/AiTutor").then((m) => ({ default: m.AiTutor })));
const Privacy = lazy(() => import("./pages/Privacy").then((m) => ({ default: m.Privacy })));
const JoinInvite = lazy(() => import("./pages/JoinInvite").then((m) => ({ default: m.JoinInvite })));
const NotFound = lazy(() => import("./pages/NotFound").then((m) => ({ default: m.NotFound })));
const SharedPreview = lazy(() => import("./pages/SharedPreview").then((m) => ({ default: m.SharedPreview })));

const PageFallback = () => {
  const { t } = useTranslation();
  return (
    <div role="status" aria-label={t("common.loading")} className="flex h-full min-h-[200px] items-center justify-center">
      <div aria-hidden className="h-8 w-8 animate-spin rounded-full border-2 border-current border-t-transparent opacity-60" />
      <span className="sr-only">{t("common.loading")}</span>
    </div>
  );
};

const withSuspense = (Component: React.ComponentType) => (
  <Suspense fallback={<PageFallback />}>
    <Component />
  </Suspense>
);

function LegacyStudyRedirect() {
  const location = useLocation();
  return <Navigate to={`/drill${location.search}`} replace />;
}

export const router = createBrowserRouter([
  {
    Component: LaunchIntentBoundary,
    errorElement: withSuspense(NotFound),
    children: [
      {
        path: "/onboarding",
        element: withSuspense(Onboarding),
      },
      {
        path: "/",
        Component: AppLayout,
        children: [
          { index: true, Component: Dashboard },
          { path: "decks", element: withSuspense(Decks) },
          { path: "decks/:id", element: withSuspense(DeckView) },
          { path: "tutor", element: withSuspense(Tutor) },
          { path: "settings", element: withSuspense(Settings) },
          { path: "profile", element: withSuspense(Profile) },
          { path: "references", element: withSuspense(References) },
          { path: "library", element: withSuspense(Library) },
          { path: "ai-tutor", element: withSuspense(AiTutor) },
        ],
      },
      {
        path: "/study",
        element: <OnboardingGuard><LegacyStudyRedirect /></OnboardingGuard>,
      },
      {
        path: "/drill",
        element: <OnboardingGuard allowInProgress>{withSuspense(Drill)}</OnboardingGuard>,
      },
      {
        path: "/join/:token",
        element: withSuspense(JoinInvite),
      },
      {
        path: "/s/:code",
        element: withSuspense(SharedPreview),
      },
      {
        path: "/privacy",
        element: withSuspense(Privacy),
      },
      {
        path: "*",
        element: withSuspense(NotFound),
      },
    ],
  },
]);
